import React from 'react';
import { motion } from 'framer-motion';

/**
 * Reusable loading spinner component
 * @param {Object} props
 * @param {string} props.size - Size of the spinner ('sm', 'md', 'lg')
 * @param {string} props.message - Optional loading message
 * @param {boolean} props.fullScreen - Whether to show as full screen overlay
 * @param {string} props.variant - Spinner variant ('default', 'magical')
 */
export default function LoadingSpinner({
  size = 'md',
  message = 'Loading...',
  fullScreen = false,
  variant = 'default'
}) {

  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12'
  };

  const MagicalSpinner = () => (
    <div className="relative">
      {/* Magic Wand */}
      <motion.div
        animate={{ 
          rotate: [0, -5, 5, -3, 3, 0],
          y: [0, -2, 0, -1, 0]
        }}
        transition={{ 
          duration: 2, 
          repeat: Infinity, 
          ease: "easeInOut"
        }}
        className="relative z-10"
      >
        <svg className={`text-primary ${sizeClasses[size]}`} fill="currentColor" viewBox="0 0 24 24">
          <rect x="2" y="20" width="16" height="2" rx="1" transform="rotate(-45 10 21)" />
          <circle cx="19" cy="5" r="1.5" fill="currentColor" />
          <line x1="4" y1="20" x2="17.5" y2="6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </motion.div>

      {/* Floating Sparks */}
      {[...Array(3)].map((_, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ 
            opacity: [0, 1, 0],
            scale: [0, 0.8, 0],
            x: [0, (i % 2 === 0 ? 8 : -8) + (i * 2)],
            y: [0, -10 - (i * 2)]
          }}
          transition={{ 
            duration: 1.5,
            repeat: Infinity,
            delay: i * 0.4,
            ease: "easeOut"
          }}
          className="absolute top-0 left-0"
        >
          <svg className="w-2 h-2 text-yellow-400" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z" />
          </svg>
        </motion.div>
      ))}

      {/* Magical Glow */}
      <motion.div
        animate={{ 
          opacity: [0.2, 0.5, 0.2],
          scale: [1, 1.1, 1]
        }}
        transition={{ 
          duration: 1.5, 
          repeat: Infinity, 
          ease: "easeInOut" 
        }}
        className="absolute inset-0 bg-primary/20 rounded-full blur-sm -z-10"
      />
    </div>
  );

  const DefaultSpinner = () => (
    <div className={`animate-spin rounded-full border-4 border-primary border-t-transparent ${sizeClasses[size]}`} />
  );

  const content = (
    <div className="flex flex-col items-center justify-center space-y-4">
      {variant === 'magical' ? <MagicalSpinner /> : <DefaultSpinner />}
      {message && (
        <motion.p 
          animate={{ opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          className="text-gray-400 text-sm font-medium"
        >
          {message}
        </motion.p>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-sm flex items-center justify-center z-50">
        {content}
      </div>
    );
  }

  return content;
} 